/**
 * Utility functions for date and year handling
 */

/**
 * Detect the logged-in user's IANA timezone from the browser/OS.
 * Falls back to 'Asia/Kolkata' if the API is unavailable (very old browsers).
 *
 * Works for every country automatically — when a user in New York opens the
 * app they see times in America/New_York; a user in Tokyo sees Asia/Tokyo;
 * the same user sees different times when traveling. No backend setting,
 * no profile field, no migration.
 *
 * @returns {string} IANA timezone identifier (e.g. 'Asia/Kolkata', 'America/New_York')
 */
export const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  } catch {
    return 'Asia/Kolkata';
  }
};

/**
 * Parse a backend date string (UTC) and format it in the logged-in user's
 * local timezone.
 *
 * Backend sends naive datetime strings (no 'Z' suffix) which JS would otherwise
 * treat as local time. This first normalizes the string to UTC, then renders
 * it in whatever timezone the user's browser reports.
 *
 * Name kept as `formatIST` for backwards compatibility with existing callers,
 * but it now respects every user's local timezone, not just IST.
 *
 * @param {string} dateString - Date string from backend (may or may not have 'Z')
 * @param {object} options - Additional Intl.DateTimeFormat options (overrides defaults)
 * @returns {string} Formatted date/time string in the user's local timezone, or 'N/A' if invalid
 */
export const formatIST = (dateString, options = {}) => {
  if (!dateString) return 'N/A';

  // Normalize: if no timezone info, treat as UTC by appending 'Z'
  const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(dateString);
  const normalized = hasTz ? dateString : dateString + 'Z';

  const date = new Date(normalized);
  if (isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: getUserTimeZone(),
    ...options,
  });
};

/**
 * Parse a backend UTC date string and format date-only in the user's local timezone.
 * Name kept as `formatISTDate` for backwards compatibility.
 *
 * @param {string} dateString - Date string from backend
 * @returns {string} Formatted date string in the user's local timezone
 */
export const formatISTDate = (dateString) => {
  if (!dateString) return 'N/A';
  const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(dateString);
  const normalized = hasTz ? dateString : dateString + 'Z';
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: getUserTimeZone(),
  });
};

/**
 * Build the displayed batch heading from `created_at` formatted in the
 * logged-in user's local timezone.
 *
 * Why: backend stores `batch_name` as a literal string like
 *   "Question Batch - 2026-04-28 10:40"
 * where the timestamp is captured from the server clock (UTC on Cloud Run).
 * To show the user's local time consistently, we ignore that embedded
 * timestamp and rebuild the heading from the real `created_at` field.
 * Falls back to the raw `batch_name` for non-standard names
 * (e.g. "Reported Question Fix - …").
 *
 * @param {object} batch - Batch object with `batch_name` and `created_at`
 * @returns {string} Heading like "Question Batch - 2026-04-28 16:10"
 */
export const formatBatchHeading = (batch) => {
  const fallback = batch?.batch_name || 'Question Batch';
  // Reported-question batches use a different naming format — leave them alone.
  if (fallback.toLowerCase().startsWith('reported')) return fallback;
  // Only rewrite the standard "Question Batch - <timestamp>" pattern.
  if (!/^question batch\s*-\s*/i.test(fallback)) return fallback;
  if (!batch?.created_at) return fallback;

  const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(batch.created_at);
  const date = new Date(hasTz ? batch.created_at : batch.created_at + 'Z');
  if (isNaN(date.getTime())) return fallback;

  // "YYYY-MM-DD hh:mm AM/PM" in the user's local timezone.
  // en-CA gives ISO-format date; en-US gives 12h time.
  const tz = getUserTimeZone();
  const datePart = date.toLocaleDateString('en-CA', { timeZone: tz });
  const timePart = date.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `Question Batch - ${datePart} ${timePart}`;
};

/**
 * Get the current year
 * @returns {number} Current year
 */
export const getCurrentYear = () => {
  return new Date().getFullYear();
};

/**
 * Get a dynamic year range (e.g., "2024-2025")
 * @param {number} startYear - Start year (optional, defaults to current year)
 * @param {number} endYear - End year (optional, defaults to current year)
 * @returns {string} Year range string
 */
export const getYearRange = (startYear = null, endYear = null) => {
  const currentYear = getCurrentYear();
  const start = startYear || currentYear;
  const end = endYear || currentYear;
  
  if (start === end) {
    return start.toString();
  }
  
  return `${start}-${end}`;
};

/**
 * Get academic year (e.g., "2024-25")
 * @param {number} year - Base year (optional, defaults to current year)
 * @returns {string} Academic year string
 */
export const getAcademicYear = (year = null) => {
  const currentYear = year || getCurrentYear();
  const nextYear = currentYear + 1;
  const shortNextYear = nextYear.toString().slice(-2);
  
  return `${currentYear}-${shortNextYear}`;
};

/**
 * Get fiscal year (e.g., "FY 2024-25")
 * @param {number} year - Base year (optional, defaults to current year)
 * @returns {string} Fiscal year string
 */
export const getFiscalYear = (year = null) => {
  const currentYear = year || getCurrentYear();
  const nextYear = currentYear + 1;
  const shortNextYear = nextYear.toString().slice(-2);
  
  return `FY ${currentYear}-${shortNextYear}`;
};

/**
 * Check if a year is a leap year
 * @param {number} year - Year to check (optional, defaults to current year)
 * @returns {boolean} True if leap year
 */
export const isLeapYear = (year = null) => {
  const checkYear = year || getCurrentYear();
  return (checkYear % 4 === 0 && checkYear % 100 !== 0) || (checkYear % 400 === 0);
};

/**
 * Get the current date in a readable format
 * @param {string} format - Date format ('short', 'long', 'iso')
 * @returns {string} Formatted date string
 */
export const getCurrentDate = (format = 'short') => {
  const now = new Date();

  switch (format) {
    case 'long':
      return now.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    case 'iso':
      return now.toISOString().split('T')[0];
    case 'short':
    default:
      return now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
  }
};

/**
 * Get relative time string (e.g., "5 minutes ago", "2 hours ago")
 * Properly handles UTC timestamps from the backend
 * @param {string} dateString - ISO date string (UTC)
 * @returns {string} Relative time string
 */
export const getTimeAgo = (dateString) => {
  if (!dateString) return 'Unknown';

  // Ensure the date string is treated as UTC by appending 'Z' if not present
  const date = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
  const now = new Date();
  const diffInMs = now - date;

  // Handle future dates or invalid dates
  if (diffInMs < 0 || isNaN(diffInMs)) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;

  // For dates older than a week, show the formatted date
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
